import { EventWithSubEvents } from '@lib/types/EventWithSubEvents';

/**
 * Utility functions for event filtering and grouping
 */

/**
 * Filters events recursively based on search text
 */
export const filterEvents = (events: Array<EventWithSubEvents>, searchText: string): Array<EventWithSubEvents> => {
  const lowerTextSearchText = searchText.toLowerCase();

  const filterRecursive = (currentEvent: EventWithSubEvents): EventWithSubEvents | null => {
    const matches = currentEvent.event.title.toLowerCase().includes(lowerTextSearchText);
    const filteredSubEvents = currentEvent.subEvents.map(filterRecursive).filter((e) => e !== null) as Array<EventWithSubEvents>;

    if (matches || filteredSubEvents.length > 0) {
      return {
        ...currentEvent,
        subEvents: filteredSubEvents,
      };
    } else {
      return null;
    }
  };

  return events.map(filterRecursive).filter((e) => e !== null) as Array<EventWithSubEvents>;
};

/**
 * Groups events by time periods with year sub-grouping for "Older" section
 */
export const groupEventsByTimePeriod = (
  events: Array<EventWithSubEvents>
): {
  recent: Array<EventWithSubEvents>;
  lastYear: Array<EventWithSubEvents>;
  older: Record<number, Array<EventWithSubEvents>>;
} => {
  const now = new Date();
  const ninetyDaysAgo = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
  const oneYearAgo = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);

  const recent: Array<EventWithSubEvents> = [];
  const lastYear: Array<EventWithSubEvents> = [];
  const olderByYear: Record<number, Array<EventWithSubEvents>> = {};

  events.forEach((event) => {
    // Extract date from event
    let eventDate: Date;

    if (event.event.eventStartTime) {
      eventDate = new Date(event.event.eventStartTime);
    } else if (event.event.eventEndTime) {
      eventDate = new Date(event.event.eventEndTime);
    } else {
      // Fallback to current date if no date is available
      eventDate = now;
    }

    if (eventDate >= ninetyDaysAgo) {
      recent.push(event);
    } else if (eventDate >= oneYearAgo) {
      lastYear.push(event);
    } else {
      const year = eventDate.getFullYear();
      if (!olderByYear[year]) {
        olderByYear[year] = [];
      }
      olderByYear[year].push(event);
    }
  });

  // Sort events within each section in descending order (newest first)
  const sortByDate = (a: EventWithSubEvents, b: EventWithSubEvents): number => {
    const getEventDate = (event: EventWithSubEvents): Date => {
      if (event.event.eventStartTime) {
        return new Date(event.event.eventStartTime);
      } else if (event.event.eventEndTime) {
        return new Date(event.event.eventEndTime);
      }
      return new Date();
    };

    return getEventDate(b).getTime() - getEventDate(a).getTime();
  };

  recent.sort(sortByDate);
  lastYear.sort(sortByDate);

  // Sort events within each year in the older section
  Object.keys(olderByYear).forEach((year) => {
    olderByYear[parseInt(year)].sort(sortByDate);
  });

  return {
    recent,
    lastYear,
    older: olderByYear,
  };
};

/**
 * Determines which sections have content for initial expanded state management
 */
export const getAvailableSections = (eventsByTimePeriod: {
  recent: Array<EventWithSubEvents>;
  lastYear: Array<EventWithSubEvents>;
  older: Record<number, Array<EventWithSubEvents>>;
}): string[] => {
  const sections = [];
  if (eventsByTimePeriod.recent.length > 0) sections.push('recent');
  if (eventsByTimePeriod.lastYear.length > 0) sections.push('lastYear');
  if (Object.keys(eventsByTimePeriod.older).length > 0) sections.push('older');
  return sections;
};

/**
 * Groups all events by year for non-collapsible view (when there are ≤10 events total)
 */
export const groupAllEventsByYear = (eventsByTimePeriod: {
  recent: Array<EventWithSubEvents>;
  lastYear: Array<EventWithSubEvents>;
  older: Record<number, Array<EventWithSubEvents>>;
}): Record<number, Array<EventWithSubEvents>> => {
  const eventsByYear: Record<number, Array<EventWithSubEvents>> = {};

  // Helper function to get year from event
  const getEventYear = (event: EventWithSubEvents): number => {
    let eventDate: Date;
    if (event.event.eventStartTime) {
      eventDate = new Date(event.event.eventStartTime);
    } else if (event.event.eventEndTime) {
      eventDate = new Date(event.event.eventEndTime);
    } else {
      eventDate = new Date();
    }
    return eventDate.getFullYear();
  };

  // Add recent events
  eventsByTimePeriod.recent.forEach((event) => {
    const year = getEventYear(event);
    if (!eventsByYear[year]) {
      eventsByYear[year] = [];
    }
    eventsByYear[year].push(event);
  });

  // Add last year events
  eventsByTimePeriod.lastYear.forEach((event) => {
    const year = getEventYear(event);
    if (!eventsByYear[year]) {
      eventsByYear[year] = [];
    }
    eventsByYear[year].push(event);
  });

  // Add older events (already grouped by year)
  Object.entries(eventsByTimePeriod.older).forEach(([yearStr, events]) => {
    const year = parseInt(yearStr);
    if (!eventsByYear[year]) {
      eventsByYear[year] = [];
    }
    eventsByYear[year].push(...events);
  });

  // Sort events within each year in descending order (newest first)
  const sortByDate = (a: EventWithSubEvents, b: EventWithSubEvents): number => {
    const getEventDate = (event: EventWithSubEvents): Date => {
      if (event.event.eventStartTime) {
        return new Date(event.event.eventStartTime);
      } else if (event.event.eventEndTime) {
        return new Date(event.event.eventEndTime);
      }
      return new Date();
    };

    return getEventDate(b).getTime() - getEventDate(a).getTime();
  };

  Object.keys(eventsByYear).forEach((year) => {
    eventsByYear[parseInt(year)].sort(sortByDate);
  });

  return eventsByYear;
};
